import { useMemo } from 'react'
import { commonIcon, resolvePublicAssetUrl, socialIcon } from '../../../lib/assets'
import '../../charts/chart.css'
import './GoalCard.css'

const TOOLTIP_ID = 'goal-card-tooltip'

const PLATFORM_ICON_MAP = {
  Instagram: socialIcon('instagram.svg'),
  Facebook: socialIcon('facebook.svg'),
  LinkedIn: socialIcon('linkedin.svg'),
  X: socialIcon('x.png'),
  TikTok: socialIcon('tiktok.svg'),
  'All Platforms': null,
  'Meta Ads': commonIcon('ads.svg'),
}

const MULTI_PLATFORM_ICONS = ['Instagram', 'Facebook', 'LinkedIn', 'X', 'TikTok']

function getRingStatus(progressRatio) {
  if (progressRatio == null || Number.isNaN(progressRatio)) return 'at-risk'
  const pct = progressRatio * 100
  if (pct >= 100) return 'achieved'
  if (pct >= 70) return 'on-track'
  if (pct >= 50) return 'needs-attention'
  return 'at-risk'
}

function getStatusLabel(status) {
  const labels = {
    achieved: 'Achieved',
    'on-track': 'On Track',
    'needs-attention': 'Needs Attention',
    'at-risk': 'At Risk',
  }
  return labels[status] ?? 'At Risk'
}

function getPlatformIcons(platform) {
  if (!platform) return []
  if (platform === 'All Platforms') {
    return MULTI_PLATFORM_ICONS.slice(0, 3).map((p) => ({ key: p, icon: PLATFORM_ICON_MAP[p], label: p }))
  }
  if (platform === 'Meta Ads') {
    return [{ key: 'meta-ads', icon: PLATFORM_ICON_MAP['Meta Ads'], label: 'Meta Ads' }]
  }
  const icon = PLATFORM_ICON_MAP[platform]
  if (icon) return [{ key: platform, icon, label: platform }]
  return []
}

/**
 * GoalCard - circular progress ring with percentage, platform icons.
 */
const GoalCard = ({
  goal,
  formatGoalValue,
  isPeriodExpired = false,
  periodEndedAcknowledged = false,
  periodEndLabel = '',
  onExtend,
}) => {
  const target = goal?.target ?? 0
  const current = goal?.current ?? 0
  const progressRatioFromGoal =
    goal?.progressRatio != null && !Number.isNaN(Number(goal.progressRatio))
      ? Number(goal.progressRatio)
      : null
  const progressRatio =
    progressRatioFromGoal != null
      ? progressRatioFromGoal
      : target > 0
        ? current / target
        : null
  const percentage =
    progressRatio != null ? Math.round(progressRatio * 100) : 0
  const ringFillPct = Math.min(100, Math.max(0, percentage))
  const ringStatus = getRingStatus(progressRatio ?? 0)
  const statusLabel = getStatusLabel(ringStatus)
  const hasTarget = target > 0 || progressRatio != null
  const progressTooltipLabel =
    goal?.goalProgressLabel != null && String(goal.goalProgressLabel).trim() !== ''
      ? String(goal.goalProgressLabel)
      : `${percentage}%`
  const showExtendAction = isPeriodExpired && periodEndedAcknowledged && typeof onExtend === 'function'

  const platformIcons = useMemo(() => getPlatformIcons(goal?.platform), [goal?.platform])

  const currentFormatted = formatGoalValue ? formatGoalValue(current, goal) : String(current)
  const targetFormatted = formatGoalValue ? formatGoalValue(target, goal) : String(target)

  const radius = 40
  const circumference = 2 * Math.PI * radius
  const strokeDashoffset = circumference * (1 - ringFillPct / 100)

  return (
    <div
      data-tooltip-id={TOOLTIP_ID}
      data-tooltip-place="bottom"
      data-goal-name={goal?.name ?? 'Goal'}
      data-goal-current={currentFormatted}
      data-goal-target={targetFormatted}
      data-goal-progress={progressTooltipLabel}
      data-goal-status={statusLabel}
      data-goal-status-type={ringStatus}
      className={`goalCard goalCard--hasTooltip${isPeriodExpired ? ' goalCard--periodEnded' : ''}`}
    >
      <div className="goalCardHeader">
        <div className="goalCardTitle">{goal?.name ?? 'Goal'}</div>
        {platformIcons.length > 0 && (
          <div className="goalCardPlatformIcons" title={platformIcons.map((p) => p.label).join(', ')}>
            {platformIcons.slice(0, 3).map((p) =>
              p.icon ? (
                <img
                  key={p.key}
                  src={resolvePublicAssetUrl(p.icon)}
                  alt={p.label}
                  className="goalCardPlatformIcon"
                  width={14}
                  height={14}
                />
              ) : null
            )}
            {platformIcons.length > 3 && (
              <span className="goalCardPlatformExtra">+{platformIcons.length - 3}</span>
            )}
          </div>
        )}
      </div>

      {isPeriodExpired && (
        <div className="goalCardPeriodEnded">
          <span className="goalCardPeriodEndedLabel">Period ended</span>
          {periodEndLabel && <span className="goalCardPeriodEndedDate">{periodEndLabel}</span>}
        </div>
      )}

      <div className="goalCardRingWrapper">
        {hasTarget ? (
          <svg className="goalCardRing" viewBox="0 0 100 100" aria-hidden="true">
            <circle
              className="goalCardRingBg"
              cx="50"
              cy="50"
              r={radius}
              fill="none"
              strokeWidth="8"
            />
            <circle
              className={`goalCardRingFill goalCardRingFill--${ringStatus}`}
              cx="50"
              cy="50"
              r={radius}
              fill="none"
              strokeWidth="8"
              strokeDasharray={circumference}
              strokeDashoffset={strokeDashoffset}
              strokeLinecap="round"
              transform="rotate(-90 50 50)"
            />
          </svg>
        ) : null}
        <div className="goalCardRingCenter">
          {hasTarget ? (
            <span className="goalCardRingValue" style={{ fontVariantNumeric: 'tabular-nums' }}>
              {percentage}%
            </span>
          ) : (
            <span className="goalCardRingNoTarget">No target set</span>
          )}
        </div>
      </div>

      {showExtendAction && (
        <button type="button" className="goalCardExtendButton" onClick={onExtend}>
          Extend goal
        </button>
      )}
    </div>
  )
}

export default GoalCard
